import type { BannerSpec } from '@invideoio/web-shared/components/WebinarBanner/banner-specs';
import { toAppSigninUrl } from '@utils/app-signin';

/**
 * Resolves the announcement-bar slides for `_app.getInitialProps`, which runs on
 * the server for the initial render and again in the browser on every
 * client-side route transition.
 *
 * The return value is the CMS answer, and the two empty-ish cases mean different
 * things to `WebinarBanner`:
 *
 * - `null` — no answer from the CMS (env not plumbed, request failed). The
 *   `banners` prop is left unset and the component renders its own hardcoded
 *   default slides.
 * - `[]` — the CMS answered "no bar" (every banner hidden or expired). The
 *   component renders nothing at all.
 *
 * Server: the shared module is imported dynamically so the server-only CMS
 * credentials never reach the client bundle. It handles its own caching and
 * failures.
 *
 * Browser: there is no request to make. `invideo.io/api/*` is routed to
 * pro-landing-pages at the edge, so an API route in this app is unreachable from
 * the public origin — a client fetch would 404 and silently downgrade a live CMS
 * banner to the hardcoded slides mid-session. Instead we re-read the value the
 * server already embedded in `__NEXT_DATA__`, which survives client-side
 * navigations, so the banner stays stable for the whole session.
 */
export async function getAnnouncementBanners(): Promise<BannerSpec[] | null> {
  if (typeof window === 'undefined') {
    const { fetchAnnouncementBanners } = await import(
      '@invideoio/web-shared/lib/API/announcement-bar'
    );
    const banners = await fetchAnnouncementBanners();
    // A slide's sign-up link goes to invideo.io, never via ai.invideo.io.
    return (
      banners?.map(banner => ({
        ...banner,
        href: toAppSigninUrl(banner.href),
      })) ?? null
    );
  }

  return (
    ((
      window as unknown as {
        __NEXT_DATA__?: { props?: Record<string, unknown> };
      }
    ).__NEXT_DATA__?.props?.announcementBanners as BannerSpec[] | null) ?? null
  );
}
